import { Component, Inject, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FaIconModule } from 'app/core/icons/fa-icons.module';
import { firstValueFrom, map, Observable, startWith } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { NoteState } from '../note-state/note.state';
import { Note, NoteUser } from '../note-state/note.model';
import { MatChipsModule } from '@angular/material/chips';
import { CreateNote, UpdateNote } from '../note-state/note.actions';
import { SharedState } from 'app/modules/shared-state/shared.state';
import { OnCanCloseDialog } from 'app/modules/shared-state/shared.actions';
import { NgxsFormDirective, NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { SharedGridState } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.state';
import { DropdownFormOptions } from 'app/modules/system-setup/dropdown-options/dropdown-state/dropdown.model';

@Component({
    selector: 'app-note-form',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatDialogModule,
        FaIconModule,
        MatAutocompleteModule,
        CommonModule,
        MatChipsModule,
        NgxsFormPluginModule,
        NgxsFormDirective,
    ],
    templateUrl: './note-form.component.html',
    styleUrl: './note-form.component.scss',
})
export class NoteFormComponent implements OnInit, OnDestroy {
    private _formBuilder = inject(FormBuilder);
    private _store = inject(Store);

    private _dialogRef = inject(MatDialogRef<NoteFormComponent>);

    public canCloseDialog$: Observable<boolean> = this._store.select(
        SharedState.getCanCloseDialog
    );
    public noteToEdit: Signal<Note> = this._store.selectSignal(
        NoteState.getNoteToEdit
    );

    public allUsers: NoteUser[] = this._store.selectSnapshot(
        NoteState.getAllUsers
    );
    public filteredUsers$: Observable<NoteUser[]>;
    public selectedUsers: NoteUser[] = []; // Stores selected users

    public noteForm: FormGroup;
    public tagsDropdownForm: Signal<DropdownFormOptions> =
        this._store.selectSignal(NoteState.getTagsDropdownForm);

    public allTags: string[];
    public filteredTags$: Observable<string[]>;
    public selectedTags: string[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
        this.noteForm = this._formBuilder.group({
            documentId: [],
            comment: ['', Validators.required],
            documentType: [{ value: '', disabled: true }, Validators.required],
            documentReference: [
                { value: '', disabled: true },
                Validators.required,
            ],
            tags: [[]],
            mentions: [[]],
            hasRead: [false],
            status: ['Unread'],
        });

        if (this.data.editNote) {
            for (const user of this.noteToEdit().mentions) {
                this.selectedUsers.push(user);
            }
            for (const tag of this.noteToEdit().tags) {
                this.selectedTags.push(tag);
            }
        }
    }

    ngOnInit() {
        //

        this.allTags = [...this.tagsDropdownForm().tags];
        this.filteredUsers$ = this.noteForm.get('mentions')?.valueChanges.pipe(
            startWith(''),
            map((value) =>
                (typeof value === 'string' ? value : '').toLowerCase()
            ),
            map((value) => this._filter(value))
        );
        this.filteredTags$ = this.noteForm.get('tags')?.valueChanges.pipe(
            startWith(''),
            map((value) =>
                (typeof value === 'string' ? value : '').toLowerCase()
            ),
            map((value) => this._filterTags(value))
        );

        this.canCloseDialog$.subscribe((canCloseDialog: boolean) => {
            if (canCloseDialog) {
                this._dialogRef.close();
            }
        });
    }

    ngOnDestroy(): void {
        this._store.dispatch(new OnCanCloseDialog(false));
    }

    private _filter(value: string): any[] {
        const filterValue = value.toLowerCase();
        return this.allUsers.filter(
            (option) =>
                (option.firstName.toLowerCase().includes(filterValue) ||
                    option.surname.toLowerCase().includes(filterValue)) &&
                !this.selectedUsers.some(
                    (user) =>
                        user.firstName === option.firstName &&
                        user.surname === option.surname
                )
        );
    }

    private _filterTags(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.allTags.filter(
            (option) =>
                option.toLowerCase().includes(filterValue) &&
                !this.selectedTags.some((tag) => tag === option)
        );
    }

    displayFn(user: any): string {
        return user ? `${user.firstName} ${user.surname}` : '';
    }

    addUser(user: any): void {
        const foundUser = this.allUsers.find(
            (u) => u.firstName === user.firstName || u.surname === user.surname
        );
        if (foundUser) {
            this.selectedUsers.push(foundUser);
        }
        this.noteForm.get('mentions')?.setValue(''); // Clear the input field
    }

    addTag(tag: string): void {
        if (tag && !this.selectedTags.includes(tag)) {
            this.selectedTags.push(tag);
            this.noteForm.get('tags')?.setValue(''); // Clear the input field
        }
    }

    removeUser(user: any): void {
        const index = this.selectedUsers.indexOf(user);
        if (index >= 0) {
            this.selectedUsers.splice(index, 1);

            // Trigger valueChanges to refresh the filtered list
            const currentValue = this.noteForm.get('mentions')?.value || '';
            this.noteForm.get('mentions')?.setValue(currentValue);
        }
    }

    removeTag(tag: string): void {
        const index = this.selectedTags.indexOf(tag);
        if (index >= 0) {
            this.selectedTags.splice(index, 1);

            // Trigger valueChanges to refresh the filtered list
            const currentValue = this.noteForm.get('tags')?.value || '';
            this.noteForm.get('tags')?.setValue(currentValue);
        }
    }

    async onSubmit() {
        const formValue = this.noteForm.getRawValue();
        formValue.mentions = this.selectedUsers;
        formValue.tags = this.selectedTags;
        if (this.data.editNote) {
            this._store.dispatch(
                new UpdateFormValue({
                    value: formValue,
                    path: 'notes.noteForm',
                })
            );
            await firstValueFrom(this._store.dispatch(
                new UpdateNote(
                    this.data.gridUUID
                        ? this.data.gridUUID
                        : this._store.selectSnapshot(
                            SharedGridState.getGridReferenceUUIDs
                        ).secondGridUUID
                )
            ));
            this.onClose();
        } else {
            this._store.dispatch(
                new UpdateFormValue({
                    value: formValue,
                    path: 'notes.noteForm',
                })
            );
            await firstValueFrom(this._store.dispatch(new CreateNote()));
            this.onClose();
        }
    }

    onClose() {
        this._dialogRef.close();
    }
}
